import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useMutation } from "@apollo/client";
import { CircularProgress } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { UPDATE_USER_FEEDBACK } from "../../app/graphql/mutations";
import { UseGetFlag } from "../../app/launchDarkly";
import { useAppSelector } from "../../app/store/hooks";
import CookingHistoryEditSkeleton from "../../components/cookingHistory/cookingHistoryEdit/CookingHistoryEditSkeleton";
import RecipeFeedBackEditTitle from "../../components/cookingHistory/cookingHistoryEdit/RecipeFeedBackEditTitle";
import useCookingHistoryEdit from "../../hooks/useCookingHistoryEdit/useCookingHistoryEdit"
import useLogRecipeEdit from "../../hooks/useLogRecipeEdit";
import { cloudinaryUploadCookingHistoryFeedback } from "../../services/restApiServices/cloudinaryUpload.services";
import { EventLogsEnum } from "../../utils/events";
import { resizeImageIfCloudinary } from "../../utils/images/cloudinaryUtils";
import styles from "./CookingHistoryEdit.module.css";
import RecipeFeedbackEdit from "./RecipeFeedbackEdit";
import RecipeRatingEdit from "./RecipeRatingEdit";
import UserCommentEdit from "./UserCommentEdit";

export const RATING_OPTIONS: Record<number, string> = {
    1: "ONE_STAR",
    2: "TWO_STAR",
    3: "THREE_STAR",
    4: "FOUR_STAR",
    5: "FIVE_STAR",
}

const cloudinaryImageUploadHandler = async (imagesLink: string[]): Promise<string[]> => {
    const copyList = [...imagesLink];
    for (let i = 0; i < copyList.length; i++) {
        if (imagesLink[i].includes("blob")) {
            const cloudinaryImageLink = await cloudinaryUploadCookingHistoryFeedback({ imageUrl: copyList[i] });
            copyList[i] = cloudinaryImageLink?.secure_url;
        }
    }
    return copyList;
}

const CookingHistoryEdit = () => {
    const { sessionId: urlSessionId } = useParams<any>();
    const history = useHistory();
    const userId = useAppSelector((state) => state.main.user?.id);

    const [rating, setRating] = useState(0);
    const [recipeId, setRecipeId] = useState<string>("");
    const [recipeName, setRecipeName] = useState("");
    const [defaultRecipeImage, setDefaultRecipeImage] = useState("");
    const [selectedFeedback, setSelectedFeedback] = useState<string[]>([]);
    const [recipeImages, setRecipeImages] = useState<string[]>([]);
    const [userComment, setUserComment] = useState<string>("");
    const [isEditFeedbackClicked, setIsEditFeedbackClicked] = useState<boolean>(false);
    const [disabledEditing, setDisabledEditing] = useState<boolean>(true);
    const { ratingOptions } = UseGetFlag();
    const { logRecipeEdit } = useLogRecipeEdit();

    const { loading } = useCookingHistoryEdit({
        sessionId: urlSessionId,
        onCompleted: (sessionData) => {
            setRating(sessionData?.rating);
            setRecipeId(sessionData?.recipeID);
            setRecipeName(sessionData?.sessionRecipe?.name ?? ""); // handle no name redirect or or throw snackbar error
            setDefaultRecipeImage(resizeImageIfCloudinary(sessionData?.sessionRecipe?.Recipe_Variations[0]?.image_url, 500, 500, 500, 500, true) ?? ""); // handle no image redirect or or throw snackbar error | added default variation check | add defulat image
            setRecipeImages(sessionData?.feedback_images ?? []);
            setUserComment(sessionData?.userComments ?? "");
            setSelectedFeedback(sessionData?.feedback ?? []);
        },
        onError: (err) => { console.error(err) }
    });

    const [updateUserFeedback, { loading: updatingUserFeedback }] = useMutation(UPDATE_USER_FEEDBACK);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleFeedbackToggle = (feedback: string) => {
        setSelectedFeedback((prev) =>
            prev.includes(feedback)
                ? prev.filter((item) => item !== feedback)
                : [...prev, feedback]
        );
    };

    const handleDeleteImage = (url: string) => {
        setRecipeImages((prev) => prev?.filter((img) => img !== url));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];

        // Create a local preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
            setRecipeImages([...recipeImages, URL.createObjectURL(file)]);
        };

        reader.readAsDataURL(file);
    };

    const onSkip = () => {
        setIsEditFeedbackClicked(false);
        history.push('/cooking-history')

        logRecipeEdit(EventLogsEnum.APP_RATING_SKIPNOW_FEEDBACK_PAGE, {
            sessionId: urlSessionId,
            userId,
        });
    }

    const onSubmit = async () => {
        const uploadedImagesIfBlob = await cloudinaryImageUploadHandler(recipeImages);

        await updateUserFeedback({
            variables: {
                sessionID: urlSessionId,
                feedback: (rating > 0) ? selectedFeedback : [],
                rating,
                userComments: userComment,
                feebBackImages: uploadedImagesIfBlob,
            },
        });

        if (!updatingUserFeedback) {
            history.push('/cooking-history')
        }

        logRecipeEdit(EventLogsEnum.APP_RATING_SUBMIT_FEEDBACK_PAGE, {
            sessionId: urlSessionId,
            userId,
            recipeId,
            eventData: {
                rating,
            }
        });

    }

    const handleEditFeedbackClick = () => {
        setIsEditFeedbackClicked(true);
        setDisabledEditing(false);

        logRecipeEdit(EventLogsEnum.APP_EDIT_FEEDBACK, {
            sessionId: urlSessionId,
            userId,
            recipeId,
        });
    }

    if (loading) {
        return <>
            <RecipeFeedBackEditTitle name={recipeName ?? "Loading"} />
            <CookingHistoryEditSkeleton />
        </>
    }

    return (
        <>
            <Grid className={styles.container} container spacing={2}>
                <RecipeFeedBackEditTitle name={recipeName ?? ""} />
                <Grid className={styles.subContainer}>
                    <RecipeRatingEdit
                        defaultRecipeImage={defaultRecipeImage}
                        disabledEditing={disabledEditing}
                        rating={rating}
                        ratingOptions={ratingOptions}
                        setRating={setRating}
                    />

                    <RecipeFeedbackEdit
                        disabledEditing={disabledEditing}
                        handleFeedbackToggle={handleFeedbackToggle}
                        rating={rating}
                        ratingOptions={ratingOptions}
                        selectedFeedback={selectedFeedback}
                    />

                    <UserCommentEdit
                        disabledEditing={disabledEditing}
                        handleDeleteImage={handleDeleteImage}
                        handleImageChange={handleImageChange}
                        recipeImages={recipeImages}
                        setUserComment={setUserComment}
                        userComment={userComment}
                    />

                </Grid>
                <Grid className={styles.footer_container}>
                    {isEditFeedbackClicked ?
                        <Grid className={styles.buttons}>
                            <Button className={styles.skipButton} onClick={onSkip} variant="text">
                                <Typography variant="ingredientText1paralell">Skip for now</Typography>
                            </Button>
                            <Button className={styles.submitButton} onClick={onSubmit} variant="text">
                                <Typography variant="ingredientText1paralell">{updatingUserFeedback ?
                                    <Grid className={styles.savingButton}>Saving <CircularProgress color="inherit" size={16} /></Grid> :
                                    'Submit'}</Typography>
                            </Button>
                        </Grid> :
                        <Grid className={styles.defaultButton} xs={12}>
                            <Button className={styles.editButton} onClick={handleEditFeedbackClick} variant="text">
                                <Typography variant="ingredientText1paralell">Edit your feedback</Typography>
                            </Button>
                        </Grid>
                    }
                </Grid>
            </Grid>
        </>
    )
}

export default CookingHistoryEdit